import { useState } from 'react';
import { useMutation } from "@apollo/client/react";
import { useFormContext } from 'react-hook-form';

import {
	DialogTitle,
	DialogActions,
	DialogContent,
	Button,
} from 'components/dialogs/DialogBox';
import ErrorMessage from 'components/ErrorMessage';
import FormIntroText from 'components/form/FormIntroText';
import Form from 'web/form/Form';
import TextField from 'web/form/TextField';
import { PhoneField, EmailField } from 'web/components/user/UserInputFields';

import { useSnackbar } from 'web/contexts/SnackbarContext';

import {
	UPDATE_ONE_USER_INPUT_WITH_VERIFICATION,
	VERIFY_ONE_USER_INPUT_WITH_TOKEN,
} from 'api/mutations/userMutations';

export default function TokenVerifyModal({
	type,
	userId,
	verificationValue,
	title,
	firstStepContent,
	lastStepContent,
	changeValueText,
}) {
	const [currentStep, setCurrentStep] = useState(0);

	return (
		<>
			{title && (
				<DialogTitle id="user-verification-dialog-title">
					{title}
				</DialogTitle>
			)}

			{currentStep === 0 && (
				<FirstStepForm
					type={type}
					userId={userId}
					verificationValue={verificationValue}
					firstStepContent={firstStepContent}
					setCurrentStep={setCurrentStep}
				/>
			)}

			{currentStep === 1 && (
				<LastStepForm
					type={type}
					userId={userId}
					verificationValue={verificationValue}
					lastStepContent={lastStepContent}
					changeValueText={changeValueText}
					setCurrentStep={setCurrentStep}
				/>
			)}
		</>
	);
}

function FirstStepForm({
	type,
	userId,
	verificationValue,
	firstStepContent,
	setCurrentStep,
}) {
	const [updateVerification, { loading, error }] = useMutation(
		UPDATE_ONE_USER_INPUT_WITH_VERIFICATION,
		{
			refetchQueries: ['currentUser'],
			errorPolicy: 'all',
		}
	);

	const handleSubmit = async ({ input }) => {
		try {
			const { data } = await updateVerification({
				variables: {
					_id: userId,
					field: type,
					value: input,
				},
			});

			if (data.updatedValue) {
				setCurrentStep(1);
			}
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Form values={{ input: verificationValue }} onSubmit={handleSubmit}>
			<ErrorMessage errors={[error]} />

			<DialogContent sx={{ paddingTop: 0 }}>
				<FormIntroText>{firstStepContent}</FormIntroText>

				{type === 'email' ? (
					<EmailField name="input" />
				) : (
					<PhoneField name="input" />
				)}
			</DialogContent>

			<DialogActions>
				<Button
					type="Mui"
					color="primary"
					variant="outlined"
					onClick={() => setCurrentStep(1)}
				>
					Jeg har allerede en kode
				</Button>

				<FirstStepSubmitButton loading={loading} />
			</DialogActions>
		</Form>
	);
}

function FirstStepSubmitButton({ loading }) {
	const {
		formState: { isDirty },
	} = useFormContext();

	return (
		<Button
			type="submit"
			variant="contained"
			color="primary"
			loading={loading}
		>
			{isDirty ? 'Oppdater og send kode' : 'Send kode'}
		</Button>
	);
}

function LastStepForm({
	type,
	userId,
	verificationValue,
	lastStepContent,
	changeValueText,
	setCurrentStep,
}) {
	const { notify } = useSnackbar();

	const [verifyUserInputWithToken, { loading, error }] = useMutation(
		VERIFY_ONE_USER_INPUT_WITH_TOKEN,
		{
			refetchQueries: ['currentUser'],
			errorPolicy: 'all',
		}
	);

	const handleSubmit = async ({ token }) => {
		try {
			const { data } = await verifyUserInputWithToken({
				variables: {
					_id: userId,
					type,
					value: verificationValue,
					token,
				},
			});

			if (data.updated) {
				notify('Verifiseringen var vellykket!');
			}
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Form onSubmit={handleSubmit}>
			<ErrorMessage errors={[error]} />

			<DialogContent sx={{ paddingTop: 0 }}>
				<FormIntroText>{lastStepContent}</FormIntroText>

				<TextField name="token" required={true} fullWidth />
			</DialogContent>

			<DialogActions>
				<Button
					type="button"
					color="primary"
					variant="outlined"
					onClick={() => setCurrentStep(0)}
				>
					{changeValueText}
				</Button>

				<Button
					type="submit"
					variant="contained"
					color="primary"
					loading={loading}
				>
					Registrer kode
				</Button>
			</DialogActions>
		</Form>
	);
}
